import { useMemo } from "react";
import { Check, X, Minus, Info, CalendarCheck2, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, tFmt } from "../../lib/crm";

const CRITERIA = [
  { key: "qual_sells_to_eu", labelKey: "criteria.sellsToEu", noteKey: "qual_sells_to_eu_note" },
  { key: "qual_volume", labelKey: "criteria.volume", noteKey: "qual_volume_note", isVolume: true },
  { key: "qual_decision_maker", labelKey: "criteria.decisionMaker", noteKey: "qual_decision_maker_note" },
  { key: "qual_pain_point", labelKey: "criteria.painPoint", noteKey: "qual_pain_point_note" },
  { key: "qual_prepayment", labelKey: "criteria.prepayment", noteKey: "qual_prepayment_note" },
];

const STATES = ["pass", "fail", "pending"];

/**
 * Live, client-side mirror of `compute_qualification` from backend. Used to give
 * the agent an instant preview before saving. Backend remains source of truth.
 */
function computeStatus(lead) {
  let passes = 0;
  let fails = 0;
  let pending = 0;
  for (const c of CRITERIA) {
    const v = (lead[c.key] || "pending").toLowerCase();
    if (v === "pass") passes += 1;
    else if (v === "fail") fails += 1;
    else pending += 1;
  }
  let status;
  if (fails >= 2) status = "disqualified";
  else if (pending > 0) status = "pending";
  else if (passes === 5) status = "qualified";
  else status = "pending";
  return { passes, fails, pending, status };
}

export default function LeadQualification({ lead, update, onScheduleClosing }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);

  const live = useMemo(() => computeStatus(lead), [lead]);
  const productType = lead.product_type || "";

  return (
    <div className="col-span-2 space-y-4" data-testid="qualification-block">
      <header className="flex flex-wrap items-end justify-between gap-3 pb-2 border-b border-white/8">
        <div>
          <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
            {t("qual.blockTitle")}
          </h3>
          <p className="text-xs text-white/40 mt-1">{t("qual.blockSubtitle")}</p>
        </div>
        <StatusPill status={live.status} passes={live.passes} t={t} language={language} />
      </header>

      {/* Product type */}
      <div className="grid sm:grid-cols-3 gap-3 items-center">
        <Label className="text-white/70 text-xs sm:col-span-1">{t("qual.productType")} *</Label>
        <div className="sm:col-span-2 flex gap-2">
          {["high_end", "accessories"].map((v) => {
            const active = productType === v;
            const label = v === "high_end" ? t("qual.productTypeHighEnd") : t("qual.productTypeAccessories");
            return (
              <button
                key={v}
                type="button"
                onClick={() => update("product_type", v)}
                data-testid={`product-type-${v}`}
                className={`px-3 py-1.5 text-xs rounded-md border transition ${
                  active
                    ? "bg-lime-400/15 border-lime-400/40 text-lime-200"
                    : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 5 criteria */}
      <div className="space-y-2">
        {CRITERIA.map((c) => (
          <CriterionRow
            key={c.key}
            criterion={c}
            value={lead[c.key] || "pending"}
            note={lead[c.noteKey] || ""}
            onStateChange={(v) => update(c.key, v)}
            onNoteChange={(v) => update(c.noteKey, v)}
            productType={productType}
            t={t}
          />
        ))}
      </div>

      {/* Notes */}
      <div>
        <Label className="text-white/70 text-xs">{t("qual.qualificationNotes")}</Label>
        <Textarea
          value={lead.qualification_notes || ""}
          onChange={(e) => update("qualification_notes", e.target.value)}
          data-testid="qualification-notes"
          className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
        />
      </div>

      {/* Disqualified reason (required when disqualified) */}
      {live.status === "disqualified" && (
        <div data-testid="disqualified-reason-block" className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
          <Label className="text-red-300 text-xs font-semibold">
            {t("qual.disqualifiedReasonLabel")} *
          </Label>
          <Textarea
            value={lead.disqualified_reason || ""}
            onChange={(e) => update("disqualified_reason", e.target.value)}
            data-testid="disqualified-reason-input"
            placeholder={t("qual.reasonRequired")}
            className="bg-white/5 border-red-500/30 text-white mt-1 min-h-[60px]"
          />
        </div>
      )}

      {/* Status banner + actions */}
      <StatusBanner
        status={live.status}
        t={t}
        onScheduleClosing={onScheduleClosing}
      />
    </div>
  );
}

function CriterionRow({ criterion, value, note, onStateChange, onNoteChange, productType, t }) {
  const labelPath = `qual.${criterion.labelKey}`;
  const label = t(`${labelPath}.label`);
  let help = t(`${labelPath}.help`);
  // Volume help text depends on product_type
  if (criterion.isVolume) {
    if (productType === "high_end") help = t("qual.criteria.volume.helpHighEnd");
    else if (productType === "accessories") help = t("qual.criteria.volume.helpAccessories");
    else help = t("qual.criteria.volume.helpUnknown");
  }
  const disabled = criterion.isVolume && !productType;

  return (
    <div
      className="rounded-lg border border-white/8 bg-white/[0.02] p-3 space-y-2"
      data-testid={`criterion-${criterion.key}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium text-white">{label}</div>
          <div className="text-[11px] text-white/40 mt-0.5 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{help}</span>
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {STATES.map((s) => (
            <StateButton
              key={s}
              state={s}
              active={value === s}
              disabled={disabled}
              onClick={() => onStateChange(s)}
              testid={`criterion-${criterion.key}-${s}`}
              t={t}
            />
          ))}
        </div>
      </div>
      {value !== "pending" && !disabled && (
        <input
          type="text"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder={t("qual.noteHint")}
          data-testid={`criterion-${criterion.key}-note`}
          className="w-full bg-white/[0.03] border border-white/10 text-white text-xs rounded-md px-2 py-1.5 focus:border-lime-400/50 focus:outline-none"
        />
      )}
    </div>
  );
}

function StateButton({ state, active, disabled, onClick, testid, t }) {
  const meta = {
    pass: {
      icon: Check,
      label: t("qual.criteriaPassed"),
      activeClasses: "bg-emerald-500/20 border-emerald-500/50 text-emerald-300",
    },
    fail: {
      icon: X,
      label: t("qual.criteriaFailed"),
      activeClasses: "bg-red-500/20 border-red-500/50 text-red-300",
    },
    pending: {
      icon: Minus,
      label: t("qual.criteriaPending"),
      activeClasses: "bg-white/10 border-white/30 text-white/70",
    },
  }[state];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={meta.label}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] transition ${
        disabled
          ? "opacity-40 cursor-not-allowed border-white/5 text-white/30"
          : active
          ? meta.activeClasses
          : "border-white/10 text-white/40 hover:border-white/30 hover:text-white/70"
      }`}
    >
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{meta.label}</span>
    </button>
  );
}

function StatusPill({ status, passes, t, language }) {
  const map = {
    qualified: {
      icon: ShieldCheck,
      color: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
      label: t("qual.statusQualified"),
    },
    disqualified: {
      icon: ShieldAlert,
      color: "border-red-500/40 bg-red-500/15 text-red-300",
      label: t("qual.statusDisqualified"),
    },
    pending: {
      icon: ShieldQuestion,
      color: "border-amber-500/40 bg-amber-500/15 text-amber-300",
      label: t("qual.statusPending"),
    },
  }[status];
  const Icon = map.icon;
  return (
    <div
      data-testid="qualification-status-pill"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${map.color}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{map.label}</span>
      <span className="text-[10px] opacity-70 mono">· {tFmt(language, "qual.scoreLabel", { passed: passes })}</span>
    </div>
  );
}

function StatusBanner({ status, t, onScheduleClosing }) {
  if (status === "qualified") {
    return (
      <div
        data-testid="qualification-banner-qualified"
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-wrap items-center gap-3"
      >
        <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-semibold text-emerald-200">{t("qual.messageQualified")}</div>
          <div className="text-[11px] text-emerald-200/60 mt-0.5">{t("qual.scheduleClosingHint")}</div>
        </div>
        {onScheduleClosing && (
          <Button
            type="button"
            onClick={onScheduleClosing}
            data-testid="schedule-closing-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          >
            <CalendarCheck2 className="w-4 h-4 mr-1" />
            {t("qual.scheduleClosing")}
          </Button>
        )}
      </div>
    );
  }
  if (status === "disqualified") {
    return (
      <div
        data-testid="qualification-banner-disqualified"
        className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3"
      >
        <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
        <div className="text-sm text-red-200">{t("qual.messageDisqualified")}</div>
      </div>
    );
  }
  return (
    <div
      data-testid="qualification-banner-pending"
      className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-center gap-3"
    >
      <ShieldQuestion className="w-5 h-5 text-amber-400 shrink-0" />
      <div className="text-sm text-amber-200">{t("qual.messagePending")}</div>
    </div>
  );
}
